import { HttpParams } from '@angular/common/http'
import { Component, Input, OnDestroy } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faCheck, faSearch } from '@fortawesome/free-solid-svg-icons'
import { CollectionView } from '@grapecity/wijmo'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellRangeEventArgs, Column, DataMap, FlexGrid } from '@grapecity/wijmo.grid'

import { BaseComponent, BidPrequalification, FeatureType, Grid, ModalSize, VpUser } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { PrequalificationComponent } from './prequalification.component'

@Component({
    selector: 'app-prequalifications',
    templateUrl: './prequalifications.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        FaIconComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        WjInputModule,
    ],
})
export class PrequalificationsComponent extends BaseComponent implements OnDestroy {
    faSearch = faSearch
    faCheck = faCheck
    active = 1

    // When rendered as a tab inside the Bid Outlook view, hide this component's own page header
    // so the outer "Bid Outlook" header is not duplicated.
    @Input() embedded = false
    allItems: BidPrequalification[] = []
    showAll = false
    grid: Grid<any>
    rangeMap!: DataMap
    ownersMap!: DataMap
    competitorsMap!: DataMap
    jobTypeMap!: DataMap

    allUserMap!: DataMap
    activeEstimatorUserMap!: DataMap
    activePMUserMap!: DataMap
    activeSuperUserMap!: DataMap

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.BidAnalyticsEdit])
    }

    constructor() {
        super()
        this.grid = new Grid<BidPrequalification>(this.services, 'id', this)
            .wrapColumnHeaders()
            .filterBy(item => [item.name])
            .setupModalEditing(
                this.services.data.bidPrequalifications,
                'Upcoming Prequalification',
                PrequalificationComponent,
                () => new BidPrequalification(),
                this,
                ModalSize.small,
            )
            .isReadOnly(!this.canEdit)
            .editOnDoubleClick(false)

        this.grid.itemAdded.on(this.filterItems.bind(this))
        this.grid.setupCustomDeletePrompt(() => {
            return this.services.modals.confirm(
                'Confirm Delete?',
                "In most cases, you should close a Prequalifiction with the 'Complete' button. A Prequalification should only be deleted in cases where it was created by accident or should not be included in analytics for some reason. Are you still sure you want to delete the Prequalification?",
            )
        }, undefined)
        this.refresh()
    }
    ngOnDestroy() {
        this.grid.itemAdded.off(this.filterItems)
    }
    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.bidPrequalifications.queryAll(),
                    this.services.data.bidRanges.queryAll(),
                    this.services.data.get('/api/VpOwner/GetOwners'),
                    this.services.data.get('/api/VpOwner/GetCompetitors'),
                    this.services.data.jobTypes.queryAll(),

                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
                    this.services.data.get('api/VpUser/GetVistaEstimatorUsers'),
                    this.services.data.get('api/VpUser/GetVistaPMUsers'),
                    this.services.data.get('api/VpUser/GetVistaSuperUsers'),

                    this.services.data.users.queryAllByAction('GetEstimators'),
                    this.services.data.get('/api/User/GetPMs'), // We can't use queryAllByAction more than once for the same API, so do get.
                    this.services.data.get('/api/User/GetSupers'), // We can't use queryAllByAction more than once for the same API, so do get.
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.grid.items = result[0]
                        this.rangeMap = new DataMap(result[1], 'id', 'name')
                        this.ownersMap = new DataMap(result[2], 'id', 'name')
                        this.competitorsMap = new DataMap(result[3], 'id', 'name')
                        this.jobTypeMap = new DataMap(result[4], 'id', 'fullName')

                        // We want the grid to show a value for any user, even inactive ones or ones who are no longer estimators.
                        this.allUserMap = new DataMap(result[5] as VpUser[], 'id', 'name')

                        // When the grid is edited, we will only show active users as options.
                        this.activeEstimatorUserMap = new DataMap(result[6] as VpUser[], 'id', 'name')
                        this.activePMUserMap = new DataMap(result[7] as VpUser[], 'id', 'name')
                        this.activeSuperUserMap = new DataMap(result[8] as VpUser[], 'id', 'name')

                        this.filterItems()
                    },
                }),
        )
    }
    filterItems() {
        setTimeout(() => {
            const view = this.grid.view as CollectionView
            view._filters.length = 1
            if (!this.showAll) {
                view._filters.push(x => !x.complete)
            }
            this.grid.selectedItem = this.grid.view.items[0]
            this.grid.refresh()
        }, 10)
    }
    cellBeginningEdit(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        // Set the column data map to just active users, so inactive users cannot be selected.
        const col = flexGrid.columns[args.col] as Column
        if (col.binding == 'estimatorHrRef' || col.binding == 'schedulerHrRef') col.dataMap = this.activeEstimatorUserMap
        if (col.binding == 'pmHrRef') col.dataMap = this.activePMUserMap
        if (col.binding == 'superHrRef') col.dataMap = this.activeSuperUserMap
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        // Set the column mapping back to all users, so inactive user names still appear.
        const col = flexGrid.columns[args.col]
        if (col.binding == 'estimatorHrRef' || col.binding == 'schedulerHrRef' || col.binding == 'pmHrRef' || col.binding == 'superHrRef')
            col.dataMap = this.allUserMap

        if (args.cancel) return
        this.updateBids([flexGrid.rows[args.row].dataItem])
    }
    pasting(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const rng = args.range
        const itemIds: string[] = []
        let row = Math.min(rng.row, rng.row2)
        const row2 = Math.max(rng.row, rng.row2)
        for (row; row <= row2; row++) {
            const dataItem = flexGrid.rows[row].dataItem
            itemIds.push(dataItem.id)
        }

        // Since the grid will sort and filter the pasted cells before the 'onCellPasted' even fires,
        // we need to keep a record of the items we are changing, so they wrong items don't get send to the server.
        flexGrid['controller'].itemIds = itemIds
    }
    pasted(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const itemsToSave = flexGrid.collectionView.items.filter(x => flexGrid['controller'].itemIds.indexOf(x.id) > -1)
        this.updateBids(itemsToSave)
        flexGrid['controller'].pastedRimnumbers = [] // clear immediately and not on promise fulfillment.
    }
    updateBids(items: BidPrequalification[]) {
        this.services.data.put('api/BidPrequalification/BulkUpdate', items).subscribe({
            error: () => {
                this.services.notification.error('The bid changes were not successfully saved')
            },
        })
    }
    complete() {
        this.services.modals
            .promptBool(
                'Mark Complete?',
                "Are you sure you want to mark the job '" +
                    this.grid.selectedItem.name +
                    "' as complete? Bid history is only created when an item is completed from Bid Outlook—completing here never adds a history record by itself.",
                'Move to Bid Outlook',
                '',
                ModalSize.small,
            )
            .then(result => {
                const params = new HttpParams().set('saveToOutlook', result[0])
                this.services.data.bidPrequalifications.edit(this.grid.selectedItem, params, 'MarkComplete').subscribe({
                    next: () => {
                        this.filterItems()
                    },
                })
            })
            .catch(() => {
                //
            })
    }
}
